import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { matchAliasSlugFromPath } from "@/config/restaurant-id";

/** Base path from any pathname: "" (legacy) or "/r/{slug}" (alias). */
export function storefrontBasePathFromPathname(pathname: string): string {
  const slug = matchAliasSlugFromPath(pathname);
  return slug ? `/r/${slug}` : "";
}

/**
 * Base path for storefront navigation: "" (legacy) or "/r/{slug}" (alias).
 */
export function useStorefrontBasePath(): string {
  const { pathname } = useLocation();
  return useMemo(() => storefrontBasePathFromPathname(pathname), [pathname]);
}

/**
 * Join storefront base with an app-relative path or path+query
 * ("/dishes" → "/r/foo/dishes", "/checkout/success?x=1" → "/r/foo/checkout/success?x=1").
 */
export function storefrontJoin(basePath: string, appPath: string): string {
  const raw = appPath.startsWith("/") ? appPath : `/${appPath}`;
  const qIndex = raw.indexOf("?");
  const pathOnly = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const query = qIndex >= 0 ? raw.slice(qIndex) : "";

  if (!basePath) {
    const legacyPath = pathOnly === "//" ? "/" : pathOnly;
    return `${legacyPath}${query}`;
  }
  if (pathOnly === "/") return `${basePath}${query}`;
  return `${basePath}${pathOnly}${query}`;
}

/**
 * Imperative storefront-aware href (for interceptors / non-React callers).
 * Derives base from `pathname` via the same helper as React hooks — no separate parser.
 */
export function storefrontHref(
  appPath: string,
  pathname: string =
    typeof window !== "undefined" ? window.location.pathname : "/"
): string {
  return storefrontJoin(storefrontBasePathFromPathname(pathname), appPath);
}

/** True when `basePath` is "" or a canonical `/r/{slug}` base (no trailing app segment). */
export function isValidStorefrontBasePath(basePath: string): boolean {
  if (basePath === "") return true;
  return storefrontBasePathFromPathname(basePath) === basePath;
}

type LocationLike = {
  pathname?: string;
  search?: string;
} | null | undefined;

const AUTH_LOOP_APP_PATHS = new Set([
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/oauth/google/complete",
  "/oauth/facebook/complete",
  "/auth/callback",
]);

function isSafeInternalPathname(pathname: string): boolean {
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return false;
  if (pathname.includes("://") || pathname.includes("\\")) return false;
  return true;
}

/**
 * Post-login (and similar) return destination.
 * Strips any storefront prefix from `fromLocation`, then re-joins with the *current*
 * storefront base so crafted cross-slug state cannot jump tenants.
 */
export function resolveStorefrontReturnTo(
  fromLocation: LocationLike,
  basePath: string,
  fallbackAppPath: string = "/"
): string {
  const fallback = storefrontJoin(basePath, fallbackAppPath);
  const rawPath = fromLocation?.pathname;
  if (typeof rawPath !== "string" || !isSafeInternalPathname(rawPath)) {
    return fallback;
  }

  let appPath = stripStorefrontPrefix(rawPath);
  const pathOnly = appPath.split("?")[0] || "/";
  if (AUTH_LOOP_APP_PATHS.has(pathOnly)) {
    return fallback;
  }

  const search =
    typeof fromLocation?.search === "string" && fromLocation.search
      ? fromLocation.search.startsWith("?")
        ? fromLocation.search
        : `?${fromLocation.search}`
      : "";
  if (search && !appPath.includes("?")) {
    appPath = `${appPath}${search}`;
  }

  return storefrontJoin(basePath, appPath);
}

const OAUTH_STOREFRONT_BASE_KEY = "oauth_storefront_base";
const OAUTH_RETURN_APP_PATH_KEY = "oauth_return_app_path";

/**
 * Persist storefront base (+ optional app return path) before leaving for Google OAuth.
 * Callback URI stays global; restore after callback. Never stores RestaurantId.
 */
export function persistOAuthStorefrontReturn(
  pathname: string,
  fromLocation?: LocationLike
): void {
  try {
    const base = storefrontBasePathFromPathname(pathname);
    sessionStorage.setItem(OAUTH_STOREFRONT_BASE_KEY, base);

    let returnApp = "/my-account";
    if (fromLocation?.pathname && isSafeInternalPathname(fromLocation.pathname)) {
      let appPath = stripStorefrontPrefix(fromLocation.pathname);
      const pathOnly = appPath.split("?")[0] || "/";
      if (!AUTH_LOOP_APP_PATHS.has(pathOnly)) {
        const search =
          typeof fromLocation.search === "string" && fromLocation.search
            ? fromLocation.search.startsWith("?")
              ? fromLocation.search
              : `?${fromLocation.search}`
            : "";
        if (search && !appPath.includes("?")) {
          appPath = `${appPath}${search}`;
        }
        returnApp = appPath || "/";
      }
    }
    sessionStorage.setItem(OAUTH_RETURN_APP_PATH_KEY, returnApp);
  } catch {
    /* private mode */
  }
}

/** Read+clear OAuth storefront restore target after global Google callback. */
export function consumeOAuthStorefrontReturn(
  fallbackAppPath: string = "/my-account"
): string {
  let base = "";
  let appPath = fallbackAppPath;
  try {
    const rawBase = sessionStorage.getItem(OAUTH_STOREFRONT_BASE_KEY);
    const rawApp = sessionStorage.getItem(OAUTH_RETURN_APP_PATH_KEY);
    sessionStorage.removeItem(OAUTH_STOREFRONT_BASE_KEY);
    sessionStorage.removeItem(OAUTH_RETURN_APP_PATH_KEY);
    if (typeof rawBase === "string" && isValidStorefrontBasePath(rawBase)) {
      base = rawBase;
    }
    if (typeof rawApp === "string" && rawApp.startsWith("/") && !rawApp.startsWith("//")) {
      const pathOnly = rawApp.split("?")[0] || "/";
      if (!AUTH_LOOP_APP_PATHS.has(pathOnly) && !rawApp.includes("://")) {
        appPath = rawApp;
      }
    }
  } catch {
    /* ignore */
  }
  return storefrontJoin(base, appPath);
}

/** Login link after failed Google callback (uses stored base if still present). */
export function peekOAuthStorefrontLoginHref(): string {
  try {
    const rawBase = sessionStorage.getItem(OAUTH_STOREFRONT_BASE_KEY);
    if (typeof rawBase === "string" && isValidStorefrontBasePath(rawBase)) {
      return storefrontJoin(rawBase, "/login");
    }
  } catch {
    /* ignore */
  }
  return storefrontHref("/login");
}

/**
 * Strip `/r/{slug}` prefix so legacy path matchers keep working.
 */
export function stripStorefrontPrefix(pathname: string): string {
  const slug = matchAliasSlugFromPath(pathname);
  if (!slug) return pathname;
  const prefix = `/r/${slug}`;
  if (pathname === prefix) return "/";
  if (pathname.startsWith(prefix + "/")) {
    const rest = pathname.slice(prefix.length);
    return rest.length ? rest : "/";
  }
  return pathname;
}
